import {
  Checkbox,
  CheckboxGroup,
  Code,
  FormControl,
  FormLabel,
  Stack,
  Text,
  VStack,
} from '@cardboard-ui/react';
import { t, Trans } from '@lingui/macro';
import React, { Suspense } from 'react';
import { SpaceIndicator } from 'components/SpaceIndicator';
import { UseFormRegister } from 'react-hook-form';
import { useLazyLoadSearchInfoQuery } from './searchInfoQuery';
import { notEmpty } from 'utils/NotEmptyFilter';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import { useFeatureFlag } from 'utils/sessionProvider';

interface AdvancedSearchOptionsProps {
  register: UseFormRegister<any>;
}

export const AdvancedSearchOptions = ({
  register,
}: AdvancedSearchOptionsProps) => {
  const isDesktop = useActiveLayout() === Layout.DESKTOP;
  const isRenameTagsEnabled = useFeatureFlag('FEATURE_RENAME_TAGS_TO_LABELS');

  const exampleTagName = isRenameTagsEnabled ? 'mylabel' : 'mytag';
  const exampleTag = <Code>{exampleTagName}</Code>;
  const tagQueryExample = isRenameTagsEnabled ? (
    <Code>label:{exampleTagName}</Code>
  ) : (
    <Code>tag:{exampleTagName}</Code>
  );

  return (
    <Stack spacing={isDesktop ? 5 : undefined}>
      <Suspense fallback="">
        <Spaces register={register} />
      </Suspense>
      <FormControl hidden>
        <FormLabel fontSize="sm">{t`Options`}</FormLabel>
        <CheckboxGroup>
          <VStack spacing={2} align="start">
            <Checkbox
              {...register('options')}
              value="favourites"
            >{t`Favourite`}</Checkbox>
            <Checkbox
              {...register('options')}
              value="pinned"
            >{t`Pinned`}</Checkbox>
            <Checkbox
              {...register('options')}
              value="owned"
            >{t`Owned by me`}</Checkbox>
          </VStack>
        </CheckboxGroup>
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">
          {isRenameTagsEnabled ? t`Labels` : t`Tags`}
        </FormLabel>
        <CheckboxGroup>
          <Text size="sm" color="gray.500">
            {isRenameTagsEnabled ? (
              <Trans>
                Enter {tagQueryExample} to search for content labeled with{' '}
                {exampleTag}.
              </Trans>
            ) : (
              <Trans>
                Enter {tagQueryExample} to search for content tagged with{' '}
                {exampleTag}.
              </Trans>
            )}
          </Text>
        </CheckboxGroup>
      </FormControl>
    </Stack>
  );
};

const Spaces = ({ register }: AdvancedSearchOptionsProps) => {
  const {
    sessionInfo: { member },
  } = useLazyLoadSearchInfoQuery();

  return (
    <FormControl>
      <FormLabel fontSize="sm">{t`Spaces`}</FormLabel>
      <CheckboxGroup>
        <VStack spacing={2} align="start">
          {(member?.spaceMemberships?.nodes || [])
            .filter(notEmpty)
            .map(({ space }) => (
              <Checkbox
                key={space.id}
                value={space.id}
                {...register('spaceIds')}
              >
                <SpaceIndicator space={space} />
              </Checkbox>
            ))}
        </VStack>
      </CheckboxGroup>
    </FormControl>
  );
};
